import { scaleBand, scaleLinear } from 'd3';
import type { ChartData } from '../../shared/analyst/api';

/**
 * Grafici del report (agentanalyst.md §11): dati presi dall'analisi salvata, nessun
 * ricalcolo nel browser. d3 calcola scale e posizioni, React disegna l'SVG.
 */

const CLASS_COLOR: Record<'positive' | 'neutral' | 'negative', string> = { positive: '#34d399', neutral: '#9ca3af', negative: '#f87171' };
const CLASS_LABEL: Record<'positive' | 'neutral' | 'negative', string> = { positive: 'positivo', neutral: 'neutro', negative: 'negativo' };
const pct = (x: number): string => `${Math.round(x * 100)}%`;

function SentimentChart({ data }: { data: Extract<ChartData, { type: 'sentiment_by_segment' }> }) {
  const width = 640;
  const rowH = 30;
  const left = 170;
  const height = data.groups.length * (rowH * 3 + 14) + 24;
  const x = scaleLinear().domain([0, 1]).range([left, width - 20]);
  let y = 8;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label={data.title}>
      {[0, 0.25, 0.5, 0.75, 1].map((t) => (
        <g key={t}>
          <line x1={x(t)} x2={x(t)} y1={0} y2={height - 18} stroke="#374151" strokeDasharray="2 3" />
          <text x={x(t)} y={height - 4} fill="#9ca3af" fontSize={10} textAnchor="middle">{pct(t)}</text>
        </g>
      ))}
      {data.groups.map((g) => {
        const top = y;
        y += g.values.length * rowH + 14;
        return (
          <g key={g.segment}>
            <text x={4} y={top + 14} fill="#e5e7eb" fontSize={12} fontWeight={600}>{g.segment}</text>
            {g.values.map((v, i) => {
              const cy = top + i * rowH + 18;
              const p = v.point.p;
              return (
                <g key={v.cls}>
                  <text x={left - 8} y={cy + 4} fill="#9ca3af" fontSize={11} textAnchor="end">{CLASS_LABEL[v.cls]}</text>
                  {p !== null && (
                    <>
                      <rect x={x(0)} y={cy - 7} width={Math.max(0, x(p) - x(0))} height={14} fill={CLASS_COLOR[v.cls]} opacity={0.75} rx={2} />
                      {v.point.lo !== null && v.point.hi !== null && (
                        <g stroke="#f9fafb" strokeWidth={1.5}>
                          <line x1={x(v.point.lo)} x2={x(v.point.hi)} y1={cy} y2={cy} />
                          <line x1={x(v.point.lo)} x2={x(v.point.lo)} y1={cy - 5} y2={cy + 5} />
                          <line x1={x(v.point.hi)} x2={x(v.point.hi)} y1={cy - 5} y2={cy + 5} />
                        </g>
                      )}
                      <text x={x(v.point.hi ?? p) + 6} y={cy + 4} fill="#d1d5db" fontSize={10}>{pct(p)} (n={v.point.n})</text>
                    </>
                  )}
                  {p === null && <text x={left} y={cy + 4} fill="#6b7280" fontSize={10}>nessuna riga valutabile</text>}
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}

function TimelineChart({ data }: { data: Extract<ChartData, { type: 'timeline' }> }) {
  const width = 720;
  const height = 220;
  const margin = { top: 12, right: 40, bottom: 40, left: 40 };
  const x = scaleBand<string>().domain(data.points.map((p) => p.bucket)).range([margin.left, width - margin.right]).padding(0.15);
  const maxRate = Math.max(1, ...data.points.map((p) => p.rows_per_hour));
  const maxNew = Math.max(1, ...data.points.map((p) => p.new_stories));
  const y = scaleLinear().domain([0, maxRate]).nice().range([height - margin.bottom, margin.top]);
  const yNew = scaleLinear().domain([0, maxNew]).nice().range([height - margin.bottom, margin.top]);
  const step = Math.max(1, Math.ceil(data.points.length / 8));
  const peaks = new Set(data.peaks);
  const line = data.points.map((p, i) => `${i === 0 ? 'M' : 'L'}${(x(p.bucket) ?? 0) + x.bandwidth() / 2},${yNew(p.new_stories)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label={data.title}>
      {y.ticks(4).map((t) => (
        <g key={t}>
          <line x1={margin.left} x2={width - margin.right} y1={y(t)} y2={y(t)} stroke="#374151" strokeDasharray="2 3" />
          <text x={margin.left - 6} y={y(t) + 3} fill="#9ca3af" fontSize={10} textAnchor="end">{t}</text>
        </g>
      ))}
      {data.points.map((p) => (
        <rect
          key={p.bucket}
          x={x(p.bucket)}
          y={y(p.rows_per_hour)}
          width={x.bandwidth()}
          height={Math.max(0, y(0) - y(p.rows_per_hour))}
          fill={peaks.has(p.bucket) ? '#f59e0b' : '#3b82f6'}
          opacity={p.partial ? 0.35 : 0.85}
        >
          <title>{`${p.bucket}: ${p.rows} righe, ${p.rows_per_hour} righe/ora, ${p.new_stories} storie nuove${p.partial ? ' (periodo parziale)' : ''}`}</title>
        </rect>
      ))}
      <path d={line} fill="none" stroke="#e879f9" strokeWidth={1.5} />
      {data.points.filter((_, i) => i % step === 0).map((p) => (
        <text key={p.bucket} x={(x(p.bucket) ?? 0) + x.bandwidth() / 2} y={height - margin.bottom + 14} fill="#9ca3af" fontSize={9} textAnchor="middle">
          {p.bucket.length > 13 ? p.bucket.slice(5, 16) : p.bucket}
        </text>
      ))}
      <text x={margin.left} y={height - 6} fill="#9ca3af" fontSize={10}>barre: righe/ora (arancio = picco, chiaro = periodo parziale) · linea: storie nuove (max {maxNew})</text>
    </svg>
  );
}

function SourceMixChart({ data }: { data: Extract<ChartData, { type: 'source_mix' }> }) {
  const total = data.families.reduce((a, f) => a + f.rows, 0) || 1;
  const sorted = [...data.families].sort((a, b) => b.rows - a.rows);
  const width = 640;
  const x = scaleLinear().domain([0, total]).range([0, width]);
  const palette = ['#60a5fa', '#a78bfa', '#34d399', '#fbbf24', '#f87171', '#22d3ee', '#9ca3af'];
  let acc = 0;
  return (
    <div>
      <svg viewBox={`0 0 ${width} 24`} className="w-full" role="img" aria-label={data.title}>
        {sorted.map((f, i) => {
          const at = acc;
          acc += f.rows;
          return <rect key={f.family} x={x(at)} y={0} width={x(f.rows)} height={24} fill={palette[i % palette.length]}><title>{`${f.family}: ${f.rows} righe`}</title></rect>;
        })}
      </svg>
      <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-300">
        {sorted.map((f, i) => (
          <span key={f.family} className="flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: palette[i % palette.length] }} />
            {f.family} {pct(f.rows / total)} ({f.rows})
          </span>
        ))}
      </div>
    </div>
  );
}

function NarrativesChart({ data }: { data: Extract<ChartData, { type: 'narratives' }> }) {
  const width = 640;
  const rowH = 34;
  const left = 230;
  const height = data.clusters.length * rowH + 24;
  const max = Math.max(0.05, ...data.clusters.flatMap((c) => [c.media_agenda_share, c.conversation_agenda_share]));
  const x = scaleLinear().domain([0, max]).nice().range([left, width - 50]);
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label={data.title}>
      {data.clusters.map((c, i) => {
        const cy = i * rowH + 12;
        return (
          <g key={c.id}>
            <text x={4} y={cy + 10} fill="#e5e7eb" fontSize={11}>
              {c.id} · {(c.label ?? 'senza etichetta').slice(0, 34)}{(c.label ?? '').length > 34 ? '…' : ''}
              <title>{`${c.label ?? ''} (${c.stories} storie, ${c.signal})`}</title>
            </text>
            <rect x={x(0)} y={cy} width={Math.max(0, x(c.media_agenda_share) - x(0))} height={10} fill="#60a5fa" rx={2} />
            <rect x={x(0)} y={cy + 12} width={Math.max(0, x(c.conversation_agenda_share) - x(0))} height={10} fill="#f472b6" rx={2} />
            <text x={x(Math.max(c.media_agenda_share, c.conversation_agenda_share)) + 6} y={cy + 16} fill="#9ca3af" fontSize={10}>
              {pct(c.media_agenda_share)} / {pct(c.conversation_agenda_share)}
            </text>
          </g>
        );
      })}
      <text x={4} y={height - 4} fill="#9ca3af" fontSize={10}>blu: quota dell'agenda dei media · rosa: quota della conversazione organica</text>
    </svg>
  );
}

export function ReportChart({ chart }: { chart: ChartData }) {
  return (
    <figure className="bg-gray-900 border border-gray-800 rounded-lg p-3">
      <figcaption className="text-xs font-semibold text-gray-300 mb-2">{chart.title}</figcaption>
      {chart.type === 'sentiment_by_segment' && <SentimentChart data={chart} />}
      {chart.type === 'timeline' && <TimelineChart data={chart} />}
      {chart.type === 'source_mix' && <SourceMixChart data={chart} />}
      {chart.type === 'narratives' && <NarrativesChart data={chart} />}
    </figure>
  );
}
